export function downloadBlob(blob: Blob | null | undefined, filename: string): void {
  if (!blob) return
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  a.remove()
  URL.revokeObjectURL(url)
}

export function pickFile(accept = 'application/pdf'): Promise<File | null> {
  return chooseFiles(accept, false).then((files) => files[0] ?? null)
}

export function pickFiles(accept = 'application/pdf'): Promise<File[]> {
  return chooseFiles(accept, true)
}

function chooseFiles(accept: string, multiple: boolean): Promise<File[]> {
  return new Promise((resolve) => {
    const input = document.createElement('input')
    input.type = 'file'
    input.accept = accept
    input.multiple = multiple
    input.style.position = 'fixed'
    input.style.left = '-9999px'
    const cleanup = (files: File[]) => {
      input.remove()
      resolve(files)
    }
    input.addEventListener('change', () => cleanup(Array.from(input.files ?? [])), { once: true })
    input.addEventListener('cancel', () => cleanup([]), { once: true })
    document.body.appendChild(input)
    input.click()
  })
}

const naturalCollator = new Intl.Collator(undefined, { numeric: true, sensitivity: 'base' })

export function naturalSortFiles(files: File[]): File[] {
  return [...files].sort((a, b) => naturalCollator.compare(a.name, b.name))
}

export function extForMime(type: string | undefined): string {
  if (type === 'image/png') return 'png'
  if (type === 'image/jpeg') return 'jpg'
  return 'pdf'
}

export function dataUrlToBlob(dataUrl: string): Blob {
  const match = /^data:([^;,]*)(;base64)?,(.*)$/s.exec(dataUrl)
  if (!match || !match[2]) throw new Error("Kein base64-Data-URL")
  const type = match[1] || 'application/octet-stream'
  const binary = atob(match[3])
  const bytes = new Uint8Array(binary.length)
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i)
  return new Blob([bytes], { type })
}

export async function toPngBlob(blob: Blob): Promise<Blob> {
  const head = new Uint8Array(await blob.slice(0, 4).arrayBuffer())
  const isPng = head[0] === 0x89 && head[1] === 0x50 && head[2] === 0x4e && head[3] === 0x47
  if (isPng) return blob.type === 'image/png' ? blob : new Blob([blob], { type: 'image/png' })
  const bitmap = await createImageBitmap(blob)
  try {
    const canvas = document.createElement('canvas')
    canvas.width = bitmap.width
    canvas.height = bitmap.height
    const ctx = canvas.getContext('2d')
    if (!ctx) throw new Error("Bild konnte nicht umgewandelt werden (kein Canvas-Kontext).")
    ctx.drawImage(bitmap, 0, 0)
    const png = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/png'))
    if (!png) throw new Error("Bild konnte nicht als PNG umgewandelt werden.")
    return png
  } finally {
    bitmap.close()
  }
}

export function fileNamePart(value: unknown): string {
  return Array.from(String(value ?? '')).filter((character) => character.charCodeAt(0) >= 32).join('')
    .replace(/[\\/:*?"<>|]/g, '')
    .trim()
    .replace(/\s+/g, '-')
}

export function personDownloadName(
  prefix: string,
  nachname: unknown,
  vorname: unknown,
  fallback: string,
  ext = 'pdf',
): string {
  const n = fileNamePart(nachname)
  const v = fileNamePart(vorname)
  if (!n && !v) return fallback
  if (!n) return `${prefix}_${v}.${ext}`
  if (!v) return `${prefix}_${n}.${ext}`
  return `${prefix}_${n}_${v}.${ext}`
}
